import { useState } from 'react';

import { CollectionWishesAdmin } from './CollectionWishesAdmin';
import { PacksAdmin } from './PacksAdmin';
import { PaymentsAdmin } from './PaymentsAdmin';
import { useAdminConsole } from './useAdminConsole';

/**
 * Экран администратора.
 *
 * Ничего не решает сам: права, пределы значений и сам факт изменения
 * проверяет сервер. Задача экрана — показать состояние продукта без
 * прикрас (в том числе «работает на заглушках») и не дать отправить то,
 * что сервер всё равно отвергнет.
 */
const METRIC_LABELS: Record<string, string> = {
  users: 'Пользователей',
  users_24h: 'Новых за сутки',
  generations: 'Генераций всего',
  generations_24h: 'Генераций за сутки',
  generations_failed_24h: 'Сбоев за сутки',
  photoshoots_24h: 'Фотосессий за сутки',
  credits_granted_24h: 'Кредитов начислено',
  credits_spent_24h: 'Кредитов потрачено',
  referrals_pending: 'Приглашений ждут первого портрета',
  referrals_rewarded: 'Приглашений награждено',
  rewarded_ads_24h: 'Наград за рекламу',
  rewarded_ads_rejected_24h: 'Показов отклонено',
  notifications_sent_24h: 'Уведомлений отправлено',
  notifications_failed_24h: 'Уведомлений не доставлено',
  notifications_queued: 'Уведомлений в очереди',
};

const PROVIDER_LABELS: Record<string, string> = {
  image: 'Генерация изображений',
  rewardedAds: 'Реклама за вознаграждение',
  notifications: 'Доставка уведомлений',
  moderation: 'Модерация фотографий',
};

export function AdminScreen() {
  const {
    overview,
    settings,
    failures,
    analytics,
    collections,
    saveSetting,
    toggleCollection,
    announceCollection,
  } = useAdminConsole();

  // Правки настроек живут в состоянии экрана до нажатия «Сохранить»:
  // случайный ввод не должен менять лимиты продукта на лету.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (overview.isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка сводки…</p>;
  }

  if (overview.isError) {
    return (
      <p className="px-6 py-8 text-danger">
        {overview.error instanceof Error ? overview.error.message : 'Доступ запрещён.'}
      </p>
    );
  }

  const metrics = overview.data?.metrics ?? {};
  const providers = overview.data?.providers ?? {};
  const managed = settings.data?.managed ?? [];
  const values = new Map((settings.data?.values ?? []).map((value) => [value.key, value.value]));
  const disabled = new Set(collections.data?.disabled ?? []);
  const announced = new Set(collections.data?.announced ?? []);

  return (
    <div className="flex flex-col gap-8 px-6 py-6">
      <section>
        <h2 className="text-lg font-semibold text-text">Службы</h2>
        <ul className="mt-3 flex flex-col gap-2">
          {Object.entries(providers).map(([key, provider]) => (
            <li key={key} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-text-2">{PROVIDER_LABELS[key] ?? key}</span>
              <span className={provider.real ? 'text-text' : 'text-danger'}>
                {provider.active}
                {provider.real ? '' : ' · заглушка'}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-text">Сводка за сутки</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Object.entries(metrics).map(([key, value]) => (
            <div key={key} className="rounded-xl border border-border px-3 py-2">
              <dt className="text-xs text-text-3">{METRIC_LABELS[key] ?? key}</dt>
              <dd className="text-lg text-text tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-text">Последние сбои</h2>

        {(failures.data?.failures ?? []).length === 0 ? (
          <p className="mt-2 text-sm text-text-2">Сбоев нет.</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2">
            {(failures.data?.failures ?? []).map((failure) => (
              <li key={failure.id} className="rounded-xl border border-border px-3 py-2 text-sm">
                <p className="text-danger">{failure.errorCode ?? 'UNKNOWN_ERROR'}</p>
                <p className="text-text-2">
                  {failure.collectionId} · {new Date(failure.createdAt).toLocaleString('ru-RU')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="text-lg font-semibold text-text">Настройки продукта</h2>

        {saveSetting.status === 'error' && (
          <p className="mt-2 text-sm text-danger">
            {saveSetting.error instanceof Error
              ? saveSetting.error.message
              : 'Не удалось сохранить.'}
          </p>
        )}

        <ul className="mt-3 flex flex-col gap-4">
          {managed.map((setting) => {
            const current = values.get(setting.key);
            const draft = drafts[setting.key] ?? String(current ?? '');
            const parsed = Number(draft);
            const valid =
              draft.trim() !== '' &&
              Number.isInteger(parsed) &&
              parsed >= setting.min &&
              parsed <= setting.max;
            const changed = parsed !== current;

            return (
              <li key={setting.key} className="flex flex-col gap-1">
                <label htmlFor={`setting-${setting.key}`} className="text-sm text-text">
                  {setting.label}
                </label>
                <span className="text-xs text-text-3">{setting.hint}</span>

                <div className="flex items-center gap-2">
                  <input
                    id={`setting-${setting.key}`}
                    type="number"
                    value={draft}
                    onChange={(event) =>
                      setDrafts((previous) => ({
                        ...previous,
                        [setting.key]: event.target.value,
                      }))
                    }
                    className="w-28 rounded-lg border border-border-strong bg-surface px-3 py-2 text-sm text-text tabular-nums"
                  />
                  <button
                    type="button"
                    disabled={!valid || !changed || saveSetting.status === 'pending'}
                    onClick={() => saveSetting.mutate({ key: setting.key, value: parsed })}
                    className="btn btn-secondary px-3 py-2 text-sm"
                  >
                    Сохранить
                  </button>
                </div>

                {!valid && (
                  <span className="text-xs text-danger">
                    Допустимо целое от {setting.min} до {setting.max}.
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <PacksAdmin />
      <PaymentsAdmin />
      <CollectionWishesAdmin />

      <section>
        <h2 className="text-lg font-semibold text-text">Коллекции в витрине</h2>
        <p className="mt-1 text-xs text-text-3">
          Скрытие убирает коллекцию из каталога. Уже созданные работы остаются доступными.
        </p>

        <ul className="mt-3 flex flex-col gap-2">
          {(collections.data?.collections ?? []).map((collection) => (
            <li key={collection.slug} className="flex items-center gap-3 text-sm">
              <input
                id={`collection-${collection.slug}`}
                type="checkbox"
                checked={!disabled.has(collection.slug)}
                disabled={toggleCollection.status === 'pending'}
                onChange={(event) =>
                  toggleCollection.mutate({
                    slug: collection.slug,
                    enabled: event.target.checked,
                  })
                }
                className="size-4 accent-accent"
              />
              <label htmlFor={`collection-${collection.slug}`} className="flex-1 text-text">
                {collection.title}
              </label>

              {announced.has(collection.slug) ? (
                <span className="text-xs text-text-3">Анонсирована</span>
              ) : (
                <button
                  type="button"
                  disabled={announceCollection.status === 'pending'}
                  onClick={() => announceCollection.mutate({ slug: collection.slug })}
                  className="btn btn-secondary px-2 py-1 text-sm"
                >
                  Анонсировать {collection.title}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-text">События за сутки</h2>

        <ul aria-label="События за сутки" className="mt-3 flex flex-col gap-1 text-sm">
          {Object.entries(analytics.data?.events ?? {}).map(([event, total]) => (
            <li key={event} className="flex justify-between gap-4">
              <span className="text-text-2">{event}</span>
              <span className="text-text tabular-nums">{total}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
